/**
 * @fileoverview Constants and configuration for Hydraulic Balancing Simulation
 * 
 * This module defines physical constants, default parameters, and configuration
 * values used throughout the hydraulic balancing simulation for multi-room
 * heat pump systems.
 * 
 * @module HydraulicBalancing/constants
 * @author HeatCraftHP Team
 * @version 2.0.0
 */

// ============================================================================
// PHYSICAL CONSTANTS
// ============================================================================

/**
 * Physical properties of water at typical heating system temperatures
 * @constant {Object}
 */
export const WATER_PROPERTIES = {
  /** Water density at 50°C [kg/m³] */
  DENSITY: 988,
  /** Specific heat capacity of water [J/(kg·K)] */
  SPECIFIC_HEAT: 4186,
  /** Dynamic viscosity at 50°C [Pa·s] */
  VISCOSITY: 0.000547,
  /** Thermal conductivity [W/(m·K)] */
  THERMAL_CONDUCTIVITY: 0.644,
};

/**
 * Physical properties of air at room temperature
 * @constant {Object}
 */
export const AIR_PROPERTIES = {
  /** Air density at 20°C [kg/m³] */
  DENSITY: 1.2,
  /** Specific heat capacity of air [J/(kg·K)] */
  SPECIFIC_HEAT: 1005,
  /** Volumetric heat capacity [Wh/(m³·K)] */
  VOLUMETRIC_HEAT_CAPACITY: 0.34,
};

/**
 * Gravitational acceleration [m/s²]
 * @constant {number}
 */
export const GRAVITY = 9.81;

// ============================================================================
// ROOM CONFIGURATION
// ============================================================================

/**
 * Default room dimensions and properties
 * @constant {Object}
 */
export const ROOM_DEFAULTS = {
  /** Room floor area [m²] */
  FLOOR_AREA: 20,
  /** Room ceiling height [m] */
  HEIGHT: 2.8,
  /** Default room volume [m³] */
  VOLUME: 56,
  /** Thermal capacitance of room [J/K] - includes air, furniture, walls */
  THERMAL_CAPACITANCE: 2e7,
  /** Default desired temperature [°C] */
  DESIRED_TEMPERATURE: 20,
  /** Default outdoor-facing U-value [W/(m²·K)] */
  DEFAULT_U_VALUE_OUTDOOR: 1.0,
};

/**
 * Radiator configuration
 * @constant {Object}
 */
export const RADIATOR_CONFIG = {
  /** Minimum number of radiator sections */
  MIN_SECTIONS: 3,
  /** Maximum number of radiator sections */
  MAX_SECTIONS: 15,
  /** Default number of radiator sections */
  DEFAULT_SECTIONS: 8,
  /** Radiator exponent for heat output calculation (EN 442 standard) */
  RADIATOR_EXPONENT: 1.3,
  /** Default heat emission coefficient per section [W/K^n] */
  DEFAULT_K_RAD: 8,
  /** Standard test conditions - supply temperature [°C] */
  TEST_SUPPLY_TEMP: 75,
  /** Standard test conditions - return temperature [°C] */
  TEST_RETURN_TEMP: 65,
  /** Standard test conditions - room temperature [°C] */
  TEST_ROOM_TEMP: 20,
};

/**
 * Valve configuration and parameters
 * @constant {Object}
 */
export const VALVE_CONFIG = {
  /** Number of preset positions (0-7 typically) */
  PRESET_POSITIONS: 7,
  /** Default Kv value [m³/h at 1 bar] */
  DEFAULT_KV_MAX: 0.5,
  /** Minimum valve opening fraction */
  MIN_OPENING: 0.05,
  /** Maximum valve opening fraction */
  MAX_OPENING: 1.0,
  /** Thermostat proportional band [K] */
  PROPORTIONAL_BAND: 2.0,
  /** Thermostat dead band [K] */
  DEAD_BAND: 0.5,
};

// ============================================================================
// PIPING SYSTEM CONFIGURATION
// ============================================================================

/**
 * Piping system parameters
 * @constant {Object}
 */
export const PIPING_CONFIG = {
  /** Default pipe length per branch [m] */
  DEFAULT_PIPE_LENGTH: 5,
  /** Default pipe inner diameter [mm] */
  DEFAULT_PIPE_DIAMETER: 15,
  /** Pipe roughness for copper/plastic [mm] */
  PIPE_ROUGHNESS: 0.0015,
  /** Darcy friction factor for typical heating pipes */
  FRICTION_FACTOR: 0.03,
  /** Floor height difference [m] */
  FLOOR_HEIGHT: 3.0,
  /** Equivalent length of fittings as multiple of straight pipe */
  FITTING_FACTOR: 1.5,
  /** Design flow rate for hydraulic balancing [kg/s] - ~5 L/min */
  DESIGN_FLOW_RATE: 0.083,
};

/**
 * System pressure configuration
 * @constant {Object}
 */
export const PRESSURE_CONFIG = {
  /** Default system differential pressure [bar] */
  DEFAULT_DP: 2,
  /** Minimum differential pressure [bar] */
  MIN_DP: 0.05,
  /** Maximum differential pressure [bar] */
  MAX_DP: 2.0,
  /** Reference pressure for Kv calculation [bar] */
  REFERENCE_DP: 1.0,
};

// ============================================================================
// HEAT PUMP CONFIGURATION
// ============================================================================

/**
 * Heat pump parameters
 * @constant {Object}
 */
export const HEAT_PUMP_CONFIG = {
  /** Compressor isentropic efficiency */
  COMPRESSOR_EFFICIENCY: 0.75,
  /** Evaporator approach temperature [K] */
  EVAPORATOR_APPROACH: 5,
  /** Condenser approach temperature [K] */
  CONDENSER_APPROACH: 5,
  /** Default supply temperature [°C] */
  DEFAULT_SUPPLY_TEMP: 55,
  /** Minimum supply temperature [°C] */
  MIN_SUPPLY_TEMP: 25,
  /** Maximum supply temperature [°C] */
  MAX_SUPPLY_TEMP: 70,
};

/**
 * Outdoor conditions
 * @constant {Object}
 */
export const OUTDOOR_CONFIG = {
  /** Default outdoor temperature [°C] */
  DEFAULT_OUTDOOR_TEMP: 5,
  /** Design outdoor temperature [°C] */
  DESIGN_OUTDOOR_TEMP: -10,
  /** Minimum outdoor temperature for simulation [°C] */
  MIN_OUTDOOR_TEMP: -20,
  /** Maximum outdoor temperature [°C] */
  MAX_OUTDOOR_TEMP: 20,
};

// ============================================================================
// SIMULATION CONFIGURATION
// ============================================================================

/**
 * Simulation timing and animation parameters
 * @constant {Object}
 */
export const SIMULATION_CONFIG = {
  /** Simulation time step [ms] */
  TIME_STEP_MS: 200,
  /** Simulated time per step [seconds] - accelerated for visualization */
  SIMULATED_TIME_STEP: 60,
  /** Maximum history length for temperature tracking */
  MAX_HISTORY_LENGTH: 20,
  /** Air changes per hour for ventilation losses */
  AIR_CHANGES_PER_HOUR: 0.5,
  /** Convergence iterations for radiator calculations */
  RADIATOR_ITERATIONS: 5,
};

/**
 * Building configuration
 * @constant {Object}
 */
export const BUILDING_CONFIG = {
  /** Maximum number of floors */
  MAX_FLOORS: 4,
  /** Maximum rooms per floor */
  MAX_ROOMS_PER_FLOOR: 3,
  /** Inter-room thermal conductance [W/(m²·K)] */
  DEFAULT_U_ROOM_TO_ROOM: 0.15,
  /** Wall area between adjacent rooms [m²] */
  WALL_AREA: 30,
  /** Room width [m] */
  ROOM_WIDTH: 5,
  /** Room depth [m] */
  ROOM_DEPTH: 4,
  /** Room height [m] */
  ROOM_HEIGHT: 2.8,
  /** Outdoor-facing wall area [m²] */
  OUTDOOR_AREA: 14,
};

// ============================================================================
// VISUALIZATION CONFIGURATION
// ============================================================================

/**
 * SVG dimensions for the building visualization
 * @constant {Object}
 */
export const SVG_DIMENSIONS = {
  WIDTH: 800,
  HEIGHT: 750,
  ROOM_WIDTH: 200,
  ROOM_HEIGHT: 150,
  MARGIN_LEFT: 100,
  MARGIN_BOTTOM: 70,
  GROUND_Y: 680,
};

/**
 * Color scheme for visualization
 * @constant {Object}
 */
export const COLORS = {
  /** Hot water / supply pipe */
  SUPPLY: 'rgba(255, 0, 0, 0.5)',
  SUPPLY_STRONG: 'rgba(255, 0, 0, 0.8)',
  /** Cold water / return pipe */
  RETURN: 'rgba(70, 130, 180, 0.5)',
  RETURN_STRONG: 'rgba(70, 130, 180, 0.8)',
  /** Temperature indicators */
  HOT: '#ff4d4f',
  WARM: '#faad14',
  NEUTRAL: '#52c41a',
  COLD: '#1890ff',
  /** UI colors */
  PRIMARY: '#0050b3',
  SECONDARY: '#1890ff',
  GRAY: 'rgba(0, 0, 0, 0.45)',
  BORDER: '#d9d9d9',
  /** Building structure colors */
  WALL: '#0B75BD',
};

// ============================================================================
// DEFAULT ROOM STATE
// ============================================================================

/**
 * Creates a default room state object
 * @param {Object} overrides - Properties to override defaults
 * @returns {Object} Room state object
 */
export const createDefaultRoom = (overrides = {}) => ({
  /** Number of radiator sections */
  nRad: RADIATOR_CONFIG.DEFAULT_SECTIONS,
  /** Desired room temperature [°C] */
  Treq: ROOM_DEFAULTS.DESIRED_TEMPERATURE,
  /** Preset valve opening (0-1) */
  valve: 3/7,
  /** Temperature history [°C] */
  Tc: [15],
  /** Thermostat valve opening (0-1) - controlled automatically */
  kThermostat: 1,
  /** Mass flow rate history [kg/s] */
  mFlow: [0],
  /** Radiator heat output history [W] */
  qrad: [0],
  /** U-value to outdoor [W/(m²·K)] */
  Uout: ROOM_DEFAULTS.DEFAULT_U_VALUE_OUTDOOR,
  /** Total area (walls, windows, roof) [m²] */
  area: BUILDING_CONFIG.WALL_AREA,
  /** Return temperature [°C] */
  Tout: 0,
  ...overrides,
});

// ============================================================================
// INITIAL BUILDING STATE
// ============================================================================

/**
 * Creates initial building configuration
 * @returns {Array<Array<Object|undefined>>} 2D array of room states
 */
export const createInitialBuilding = () => [
  [
    createDefaultRoom({ nRad: 5, Tc: [15] }),
    createDefaultRoom({ nRad: 10, Tc: [18] }),
    undefined,
  ],
];

// ============================================================================
// FORMULA DOCUMENTATION
// ============================================================================

/**
 * Key formulas used in the simulation:
 * 
 * 1. RADIATOR HEAT OUTPUT (EN 442):
 *    Q = K × (ΔT_m)^n
 *    Where: ΔT_m = (T_supply + T_return)/2 - T_room
 *           n ≈ 1.3 (radiator exponent)
 *           K = heat emission coefficient [W/K^n]
 * 
 * 2. VALVE FLOW EQUATION:
 *    V̇ = Kv × √(Δp)
 *    Where: Kv = flow coefficient [m³/h per √bar]
 *           Δp = pressure drop across valve [bar]
 * 
 * 3. PIPE PRESSURE LOSS (Darcy-Weisbach):
 *    Δp = f × (L/D) × (ρv²/2)
 *    Where: f = friction factor
 *           L = pipe length [m]
 *           D = pipe diameter [m]
 * 
 * 4. HEAT PUMP COP (Carnot with efficiency):
 *    COP = η_c × T_hot / (T_hot - T_cold)
 *    Where: η_c = compressor efficiency
 *           T values in Kelvin
 * 
 * 5. HYDRAULIC BALANCING PRESET:
 *    Kv_preset = Kv_max × (1 - h/H_total × correction_factor)
 *    Adjusts valve opening based on floor height to ensure equal flow
 * 
 * 6. TRANSMISSION HEAT LOSS:
 *    Q_loss = U × A × (T_room - T_outdoor)
 *    Where: U = thermal transmittance [W/(m²·K)]
 *           A = surface area [m²]
 * 
 * 7. VENTILATION HEAT LOSS:
 *    Q_vent = 0.34 × V × n × (T_room - T_outdoor)
 *    Where: V = room volume [m³]
 *           n = air changes per hour [1/h]
 */
